import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, ArrowsClockwise, TextAa, ArrowsInSimple, ListBullets, BookOpen, Lightning, Check, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import useIA from '../../hooks/useIA';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AI_ACTIONS = [
  { key: 'ameliorer', label: 'Améliorer', icon: Sparkle, desc: 'Plus précis et normatif' },
  { key: 'completer', label: 'Compléter', icon: TextAa, desc: 'Développer le texte' },
  { key: 'raccourcir', label: 'Raccourcir', icon: ArrowsInSimple, desc: 'Synthétiser' },
  { key: 'formater_cctp', label: 'Formater CCTP', icon: ListBullets, desc: 'Structure normative' },
  { key: 'ajouter_normes', label: 'Ajouter normes', icon: BookOpen, desc: 'DTU/NF applicables' },
  { key: 'generer', label: 'Générer', icon: Lightning, desc: 'Depuis zéro' },
];

export default function CCTPAIToolbar({ chantierId, lotName, posteDesignation, currentText, onAccept }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [diff, setDiff] = useState(null);
  const toolbarRef = useRef(null);
  // RP96 — réglage IA du cabinet : bouton grisé avec explication si non disponible
  const ia = useIA();
  const iaOff = !ia.disponible('redaction');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        if (!diff) setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [diff]);

  const handleAction = async (action) => {
    setLoading(true);
    setLoadingAction(action);
    try {
      const { data } = await axios.post(`${API_URL}/api/ia/ameliorer-texte`, {
        chantier_id: chantierId,
        action,
        texte_existant: currentText,
        contexte_lot: `Lot: ${lotName} / Poste: ${posteDesignation}`,
        type_document: 'cctp',
      }, { withCredentials: true });
      setDiff({ original: data.texte_original, improved: data.texte_ameliore, action: data.action_effectuee });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
      setLoadingAction('');
    }
  };

  const acceptDiff = () => {
    if (diff) {
      onAccept(diff.improved);
      setDiff(null);
      setOpen(false);
      toast.success('Texte appliqué');
    }
  };

  const rejectDiff = () => {
    setDiff(null);
  };

  if (diff) {
    return (
      <div className="border border-[#6366F1]/30 bg-white mt-2 mb-2" data-testid="cctp-ai-diff">
        <div className="px-3 py-2 bg-[#6366F1]/5 border-b border-[#6366F1]/20 flex items-center justify-between">
          <span className="text-xs font-medium text-[#6366F1] flex items-center gap-1">
            <Sparkle size={14} weight="fill" /> Proposition IA
          </span>
          <div className="flex gap-1">
            <Button size="sm" onClick={acceptDiff} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3" data-testid="accept-ai-diff">
              <Check size={14} className="mr-1" /> Accepter
            </Button>
            <Button size="sm" variant="ghost" onClick={rejectDiff} className="h-7 text-xs px-2" data-testid="reject-ai-diff">
              <X size={14} />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-[#E0E0DB]">
          <div className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] mb-2">Original</p>
            <div className="text-xs text-[#6B6B6B] whitespace-pre-wrap line-through decoration-red-300 max-h-60 overflow-y-auto">{diff.original || '(vide)'}</div>
          </div>
          <div className="p-3 bg-emerald-50/30">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600 mb-2">Proposition</p>
            <div className="text-xs text-[#2C2C2C] whitespace-pre-wrap max-h-60 overflow-y-auto">{diff.improved}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative inline-block" ref={toolbarRef}>
      <button
        onClick={() => setOpen(!open)}
        disabled={iaOff}
        title={ia.raison('redaction')}
        className="inline-flex items-center gap-1 text-[10px] text-[#6366F1] hover:text-[#4F46E5] hover:bg-[#6366F1]/10 px-1.5 py-0.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="cctp-ai-toggle"
      >
        <Sparkle size={12} weight="fill" /> IA
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 bg-white border border-[#E0E0DB] shadow-lg w-52" data-testid="cctp-ai-toolbar">
          <div className="p-2 border-b border-[#E0E0DB] bg-[#F5F5F3]">
            <p className="text-[10px] text-[#6B6B6B] font-medium uppercase tracking-wider">Assistance IA</p>
          </div>
          <div className="py-1">
            {AI_ACTIONS.map(({ key, label, icon: Icon, desc }) => (
              <button
                key={key}
                onClick={() => handleAction(key)}
                disabled={loading}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F5F5F3] disabled:opacity-50 transition-colors"
                data-testid={`cctp-ai-action-${key}`}
              >
                <Icon size={14} className="text-[#6366F1] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#2C2C2C]">{label}</p>
                  <p className="text-[10px] text-[#9A9A9A]">{desc}</p>
                </div>
                {loadingAction === key && <ArrowsClockwise size={12} className="animate-spin text-[#6366F1] ml-auto" />}
              </button>
            ))}
          </div>
          {loading && (
            <div className="px-3 py-2 border-t border-[#E0E0DB] bg-[#6366F1]/5">
              <p className="text-[10px] text-[#6366F1] flex items-center gap-1">
                <ArrowsClockwise size={10} className="animate-spin" /> L'IA améliore votre descriptif...
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
